/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * Event naming convention: PageName_elementName_action
 * Examples:
 * - HomePage_cta_get_started
 * - DashboardPage_board_card_open
 * - Navbar_button_logout
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type ClickEventName = 
  // HomePage
  | 'HomePage_cta_get_started'
  | 'HomePage_cta_try_generator'
  | 'HomePage_generator_fill_sample'
  | 'HomePage_generator_generate_demo'
  | 'HomePage_generator_generate_real'
  | 'HomePage_generator_open_full'
  | 'HomePage_cta_ai_demo'
  | 'HomePage_cta_bottom'
  | 'HomePage_google_auth'
  // LoginPage
  | 'LoginPage_logo_navigate_home'
  | 'LoginPage_link_register'
  | 'LoginPage_password_toggle'
  | 'LoginPage_link_forgot_password'
  | 'LoginPage_button_sign_in'
  | 'LoginPage_google_auth'
  | 'LoginPage_link_terms'
  | 'LoginPage_link_privacy'
  | 'LoginPage_button_sso_login'
  // RegisterPage
  | 'RegisterPage_logo_navigate_home'
  | 'RegisterPage_link_login'
  | 'RegisterPage_password_toggle'
  | 'RegisterPage_plan_select'
  | 'RegisterPage_button_create_account'
  | 'RegisterPage_google_auth'
  | 'RegisterPage_link_terms'
  | 'RegisterPage_link_privacy'
  // ForgotPasswordPage
  | 'ForgotPasswordPage_logo_navigate_home'
  | 'ForgotPasswordPage_link_login'
  | 'ForgotPasswordPage_button_send_reset'
  // ResetPasswordPage
  | 'ResetPasswordPage_logo_navigate_home'
  | 'ResetPasswordPage_link_forgot_password'
  | 'ResetPasswordPage_password_toggle'
  | 'ResetPasswordPage_button_reset'
  | 'ResetPasswordPage_link_login'
  // DashboardPage
  | 'DashboardPage_button_new_board'
  | 'DashboardPage_link_upgrade_plan'
  | 'DashboardPage_board_menu_edit'
  | 'DashboardPage_board_menu_delete'
  | 'DashboardPage_filter_select'
  | 'DashboardPage_team_filter_select'
  | 'DashboardPage_button_create_board_empty'
  | 'DashboardPage_board_card_open'
  | 'DashboardPage_banner_view_insights'
  | 'DashboardPage_button_retry_boards'
  // OrganizationPage
  | 'OrganizationPage_button_back_dashboard'
  | 'OrganizationPage_tab_overview'
  | 'OrganizationPage_tab_people'
  | 'OrganizationPage_tab_teams'
  | 'OrganizationPage_tab_boards'
  | 'OrganizationPage_link_subscription'
  | 'OrganizationPage_button_invite_member'
  | 'OrganizationPage_subtab_members'
  | 'OrganizationPage_subtab_invites'
  | 'OrganizationPage_member_action_menu'
  | 'OrganizationPage_member_edit_role'
  | 'OrganizationPage_member_remove'
  | 'OrganizationPage_member_transfer_ownership'
  | 'OrganizationPage_invite_resend'
  | 'OrganizationPage_invite_cancel'
  | 'OrganizationPage_invite_modal_send'
  | 'OrganizationPage_edit_role_modal_update'
  // TeamsPage
  | 'TeamsPage_button_new_team'
  | 'TeamsPage_link_upgrade_plan'
  | 'TeamsPage_button_create_first_team'
  | 'TeamsPage_button_delete_team'
  | 'TeamsPage_button_view_members'
  | 'TeamsPage_button_invite_link'
  | 'TeamsPage_create_modal_submit'
  | 'TeamsPage_create_modal_cancel'
  | 'TeamsPage_invite_modal_copy_url'
  | 'TeamsPage_members_modal_remove_member'
  // TeamJoinPage
  | 'TeamJoinPage_logo_navigate'
  | 'TeamJoinPage_link_sign_in'
  | 'TeamJoinPage_button_decline'
  | 'TeamJoinPage_button_join'
  // ActionsPage
  | 'ActionsPage_filter_select'
  | 'ActionsPage_link_board'
  | 'ActionsPage_item_status_toggle'
  | 'ActionsPage_item_delete'
  | 'ActionsPage_item_status_select'
  // ProfilePage
  | 'ProfilePage_avatar_remove_image'
  | 'ProfilePage_avatar_color_select'
  | 'ProfilePage_avatar_random_color'
  | 'ProfilePage_button_update_password'
  | 'ProfilePage_button_cancel'
  | 'ProfilePage_button_save'
  // PricingPage
  | 'PricingPage_billing_monthly'
  | 'PricingPage_billing_yearly'
  | 'PricingPage_plan_cta'
  // SubscriptionPage
  | 'SubscriptionPage_billing_toggle'
  | 'SubscriptionPage_plan_select'
  | 'SubscriptionPage_button_upgrade'
  | 'SubscriptionPage_button_cancel'
  // TemplatesPage
  | 'TemplatesPage_category_select'
  | 'TemplatesPage_template_select'
  | 'TemplatesPage_modal_use_template'
  | 'TemplatesPage_button_use_template'
  // TemplateDetailPage
  | 'TemplateDetailPage_link_back'
  | 'TemplateDetailPage_button_use_template'
  | 'TemplateDetailPage_link_browse'
  // OnboardingPage
  | 'OnboardingPage_plan_select'
  | 'OnboardingPage_button_continue'
  | 'OnboardingPage_button_skip'
  // FreeAiReportPage
  | 'FreeAiReportPage_tab_select'
  | 'FreeAiReportPage_button_unlock'
  | 'FreeAiReportPage_button_sign_in'
  | 'FreeAiReportPage_button_try_sample'
  | 'FreeAiReportPage_template_select'
  | 'FreeAiReportPage_button_generate'
  | 'FreeAiReportPage_button_reset'
  | 'FreeAiReportPage_button_print'
  | 'FreeAiReportPage_button_create_account'
  | 'FreeAiReportPage_google_auth'
  // BlogPage
  | 'BlogPage_link_post'
  | 'BlogPage_link_cta'
  // BlogPostPage
  | 'BlogPostPage_link_back'
  | 'BlogPostPage_link_related'
  // FeaturesPage
  | 'FeaturesPage_link_cta'
  // AboutPage
  | 'AboutPage_link_cta'
  // CanvasBoard
  | 'CanvasBoard_button_board_dropdown'
  | 'CanvasBoard_board_select'
  | 'CanvasBoard_button_actions_panel'
  | 'CanvasBoard_report_history_toggle'
  | 'CanvasBoard_report_history_select'
  | 'CanvasBoard_button_export'
  | 'CanvasBoard_button_timer'
  | 'CanvasBoard_button_ai_report'
  | 'CanvasBoard_button_grouping'
  | 'CanvasBoard_card_add'
  | 'CanvasBoard_card_edit'
  | 'CanvasBoard_card_delete'
  | 'CanvasBoard_card_vote_up'
  | 'CanvasBoard_card_vote_down'
  // Navbar
  | 'Navbar_link_logo'
  | 'Navbar_link_menu'
  | 'Navbar_button_mobile_menu'
  | 'Navbar_button_sidebar_toggle'
  | 'Navbar_button_user_dropdown'
  | 'Navbar_link_profile'
  | 'Navbar_button_logout'
  | 'Navbar_link_cta'
  | 'Navbar_dropdown_product'
  | 'Navbar_dropdown_solutions'
  | 'Navbar_dropdown_resources'
  | 'Navbar_dropdown_link'
  | 'Navbar_google_auth'
  // Landing Components
  | 'HeroSection_link_cta'
  | 'HeroSection_tab_board'
  | 'HeroSection_tab_ai'
  | 'FeaturesSection_feature_select'
  | 'FAQSection_faq_toggle'
  | 'CTASection_link_cta'
  | 'SolutionsSection_solution_select'
  | 'FooterSection_link_logo'
  | 'FooterSection_link_nav'
  | 'FooterSection_link_social'
  // Components
  | 'AiReportButton_click'
  | 'AiReportButton_modal_get_report'
  | 'AiReportPanel_button_close'
  | 'BoardTemplateSelector_template_select'
  | 'ExportBoard_button_export'
  | 'ExportBoard_format_select'
  | 'BoardTimer_button_start'
  | 'BoardTimer_button_pause'
  | 'BoardTimer_button_stop'
  | 'BoardTimer_button_resume'
  | 'FacilitatorControls_button_toggle'
  | 'FacilitatorControls_phase_change'
  | 'FacilitatorControls_toggle_private'
  | 'FacilitatorControls_reveal_cards'
  | 'FacilitatorControls_toggle_anonymous'
  | 'FacilitatorControls_toggle_readonly'
  | 'ActionItemsPanel_button_add'
  | 'ActionItemsPanel_button_edit'
  | 'ActionItemsPanel_button_delete'
  | 'ActionItemsPanel_status_select'
  | 'ActionItemsList_button_add'
  | 'ActionItemsList_item_edit'
  | 'ActionItemsList_item_delete'
  | 'AuthModal_tab_login'
  | 'AuthModal_tab_register'
  | 'AuthModal_button_submit'
  | 'AuthModal_google_auth'
  | 'ConfirmationModal_button_confirm'
  | 'ConfirmationModal_button_cancel'
  | 'LimitBanner_link_upgrade'
  | 'GoogleAuthButton_click';

interface ClickEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: unknown;
}

/**
 * Track a click event in Google Analytics 4
 * 
 * @param eventName - The event name following the format: PageName_elementName_action
 * @param params - Optional additional parameters for the event
 * 
 * @example
 * // Basic usage
 * trackClick('HomePage_cta_get_started');
 * 
 * @example
 * // With additional parameters
 * trackClick('DashboardPage_board_card_open', { event_label: 'board-123' });
 * 
 * @example
 * // In an onClick handler
 * <button onClick={() => trackClick('LoginPage_button_sign_in')}>Sign In</button>
 */
export function trackClick(eventName: ClickEventName, params?: ClickEventParams): void {
  if (typeof window === 'undefined' || !window.gtag) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics]', eventName, params);
    }
    return;
  }

  const [page, ...rest] = eventName.split('_');
  const element = rest.join('_');

  window.gtag('event', 'click', {
    event_category: page,
    event_label: element,
    click_element: eventName,
    ...params,
  });
}

/**
 * Track a custom event in Google Analytics 4
 * 
 * @param eventName - Custom event name
 * @param params - Event parameters
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.gtag) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics Event]', eventName, params);
    }
    return;
  }

  window.gtag('event', eventName, params);
}

/**
 * Track page view in Google Analytics 4
 * 
 * @param pagePath - The page path
 * @param pageTitle - The page title
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

/**
 * Create a click handler that tracks the event and then calls the original handler
 * 
 * @param eventName - The event name to track
 * @param handler - Optional original click handler
 * @param params - Optional additional parameters
 * 
 * @example
 * <button onClick={withTracking('LoginPage_button_sign_in', handleSignIn)}>
 *   Sign In
 * </button>
 */
export function withTracking<T extends (...args: unknown[]) => unknown>(
  eventName: ClickEventName,
  handler?: T,
  params?: ClickEventParams
): (...args: Parameters<T>) => ReturnType<T> | void {
  return (...args: Parameters<T>) => {
    trackClick(eventName, params);
    if (handler) {
      return handler(...args) as ReturnType<T>;
    }
  };
}

export type { ClickEventName, ClickEventParams };
